import { authStore } from "@/store/auth.js";
import { featureFlags, whenFeatureFlagsReady } from "@/store/featureFlags.js";
import { Routes } from "@/constants/routes.js";

/**
 * How long a landing decision will wait for the flag store to report.
 *
 * Every millisecond here is time a learner spends looking at nothing after
 * sign-in, so the bound is short and the fallback is the destination the app
 * used before Daily Play existed. An allowlisted learner whose flags are slow
 * lands on My Chats and reaches Today from the tab bar; nobody is stranded.
 */
export const LANDING_FLAG_WAIT_MS = 1500;

/**
 * Where the app goes after an explicit sign-in, or after a guard bounces a
 * signed-in visitor off a route they do not belong on.
 *
 * This replaces the seven sites that each hardcoded `Routes.DASHBOARD_CHAT`
 * (two router guards, five sign-in completions in authUtils). It decides only
 * the DEFAULT destination: an explicit `?redirect=` that the router guard set
 * when it bounced an unauthenticated visitor off a protected route is honoured
 * by the caller and never reaches this function.
 *
 * It must be called only after sign-in has completed, because it reads the
 * signed-in uid to decide which allowlist evidence it is waiting for. Calling
 * it before that reads `null`, waits for the signed-out readiness state, and
 * answers My Chats, which is the wrong answer for an allowlisted learner.
 *
 * @returns {Promise<string>} a path from `Routes`.
 */
export async function landingRoute() {
  return (await dailyPlayAllowed())
    ? Routes.DASHBOARD_TODAY
    : Routes.DASHBOARD_CHAT;
}

/**
 * Whether this signed-in learner may be shown Daily Play at all.
 *
 * Shared by the landing decision above and by the router's guard on the two
 * Daily Play routes, so the two can never answer differently about the same
 * learner in the same moment.
 *
 * It waits, briefly, because the flag store fills from RTDB listeners and a
 * cold start asks this question before they have reported.
 *
 * It answers NO when the bound expires, and that is deliberate. "We never
 * heard" and "the flag is off" give the same answer today, because the store
 * itself fails closed, but they are written as two branches: if a later flag
 * ever defaults to on, not having heard must keep meaning no rather than
 * inheriting that default. The cost of that false no is bounded and it
 * self-corrects in front of the learner, because the tab bar's Today entry is
 * reactive: they land on My Chats, the flag arrives, the tab appears, and the
 * round is one tap away.
 *
 * @returns {Promise<boolean>}
 */
export async function dailyPlayAllowed() {
  const uid = authStore.getUserId() ?? null;
  let timer = null;
  const ready = whenFeatureFlagsReady(uid);
  const reported = await Promise.race([
    ready.then(() => true),
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(false), LANDING_FLAG_WAIT_MS);
    }),
  ]);
  if (timer !== null) clearTimeout(timer);
  // Walking away from a promise does not drop the waiter behind it, and the
  // store cannot sweep one on its own: its sweep runs when a listener reports,
  // which is exactly what is not happening when this bound expires.
  ready.cancel?.();
  if (!reported) return false;
  return featureFlags.dailyPlay === true;
}

/**
 * Whether a path is one of Daily Play's two routes.
 *
 * Matched on a SEGMENT boundary rather than with a bare `startsWith`, so that a
 * future `/dashboard/todaysomethingelse` is not swept into this feature's gate
 * by sharing a prefix with it.
 */
export function isDailyPlayRoute(path) {
  if (typeof path !== "string") return false;
  const base = Routes.DASHBOARD_TODAY;
  return path === base || path.startsWith(`${base}/`);
}
